import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { useNotifications } from "../../../../../reactQuery/notifications";
import { getResponseData } from "../../../../../reactQuery/response";
import { markNotificationAsRead } from "../../../../../services/apiNotifications";
import {
  currentUserToken,
  getAPIBaseUrlVersion1,
} from "../../../../../utils/constants";
import {
  getNotificationId,
  getNotificationRoute,
  mergeNotifications,
  normalizeSocketNotification,
} from "../notificationUtils";

const SOCKET_PING_INTERVAL_MS = 25000;
const SOCKET_PONG_TIMEOUT_MS = 10000;
const SOCKET_RECONNECT_BASE_DELAY_MS = 1000;
const SOCKET_MAX_RECONNECT_DELAY_MS = 30000;

export function useNotificationState() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [liveNotifications, setLiveNotifications] = useState([]);
  const [locallyReadIds, setLocallyReadIds] = useState([]);
  const { notifications, isLoading, isError, refetch } = useNotifications();
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pongTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const lastPongAtRef = useRef(Date.now());
  const shouldReconnectRef = useRef(true);
  const isConnectingRef = useRef(false);
  const connectSocketRef = useRef(() => {});

  const serverNotifications = useMemo(
    () => getResponseData(notifications, []),
    [notifications]
  );

  const items = useMemo(
    () => mergeNotifications(serverNotifications, liveNotifications),
    [liveNotifications, serverNotifications]
  );

  const unreadCount = useMemo(
    () =>
      items.filter((notification) => {
        const notificationId = getNotificationId(notification);

        return !notification.is_read && !locallyReadIds.includes(notificationId);
      }).length,
    [items, locallyReadIds]
  );

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const clearHeartbeatTimers = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    const socket = socketRef.current;
    socketRef.current = null;

    if (!socket) {
      return;
    }

    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;

    if (
      socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING
    ) {
      socket.close();
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!shouldReconnectRef.current || reconnectTimeoutRef.current) {
      return;
    }

    const reconnectDelay = Math.min(
      SOCKET_RECONNECT_BASE_DELAY_MS *
        2 ** Math.max(reconnectAttemptsRef.current - 1, 0),
      SOCKET_MAX_RECONNECT_DELAY_MS
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      reconnectAttemptsRef.current += 1;

      if (shouldReconnectRef.current) {
        refetch();
        connectSocketRef.current();
      }
    }, reconnectDelay);
  }, [refetch]);

  const connectSocket = useCallback(() => {
    const token = currentUserToken();

    if (!token || !shouldReconnectRef.current || isConnectingRef.current) {
      return;
    }

    const existingSocket = socketRef.current;
    if (
      existingSocket &&
      (existingSocket.readyState === WebSocket.OPEN ||
        existingSocket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    clearReconnectTimeout();
    clearHeartbeatTimers();
    closeSocket();
    isConnectingRef.current = true;

    const websocketBaseUrl = getAPIBaseUrlVersion1().replace(/^http/, "ws");
    const socket = new WebSocket(
      `${websocketBaseUrl}/notifications/ws?token=${encodeURIComponent(token)}`
    );

    socketRef.current = socket;

    const sendPing = () => {
      if (socketRef.current !== socket || socket.readyState !== WebSocket.OPEN) {
        return;
      }

      try {
        socket.send("ping");

        if (pongTimeoutRef.current) {
          clearTimeout(pongTimeoutRef.current);
        }

        pongTimeoutRef.current = setTimeout(() => {
          if (socketRef.current === socket) {
            closeSocket();
            scheduleReconnect();
          }
        }, SOCKET_PONG_TIMEOUT_MS);
      } catch (error) {
        console.error("Notification websocket ping failed", error);
        closeSocket();
        scheduleReconnect();
      }
    };

    socket.onopen = () => {
      if (socketRef.current !== socket) {
        return;
      }

      isConnectingRef.current = false;
      reconnectAttemptsRef.current = 0;
      lastPongAtRef.current = Date.now();
      refetch();
      sendPing();
      pingIntervalRef.current = setInterval(sendPing, SOCKET_PING_INTERVAL_MS);
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);

        if (payload?.type === "pong" || payload?.type === "connected") {
          lastPongAtRef.current = Date.now();

          if (pongTimeoutRef.current) {
            clearTimeout(pongTimeoutRef.current);
            pongTimeoutRef.current = null;
          }

          return;
        }

        const incomingNotification = normalizeSocketNotification(payload);

        if (!incomingNotification) {
          return;
        }

        setLiveNotifications((previousNotifications) =>
          mergeNotifications(previousNotifications, [incomingNotification])
        );
      } catch (error) {
        console.error("Failed to parse notification websocket message", error);
      }
    };

    socket.onerror = (error) => {
      console.error("Notification websocket error", error);
    };

    socket.onclose = () => {
      if (socketRef.current === socket) {
        socketRef.current = null;
      }

      isConnectingRef.current = false;
      clearHeartbeatTimers();
      scheduleReconnect();
    };
  }, [
    clearHeartbeatTimers,
    clearReconnectTimeout,
    closeSocket,
    refetch,
    scheduleReconnect,
  ]);

  connectSocketRef.current = connectSocket;

  useEffect(() => {
    const token = currentUserToken();

    if (!token) {
      shouldReconnectRef.current = false;
      return undefined;
    }

    shouldReconnectRef.current = true;
    connectSocket();

    const restoreSocketConnection = () => {
      if (!shouldReconnectRef.current) {
        return;
      }

      const socket = socketRef.current;
      const isSocketOpen = socket?.readyState === WebSocket.OPEN;
      const pongAge = Date.now() - lastPongAtRef.current;

      if (!isSocketOpen || pongAge > SOCKET_PING_INTERVAL_MS + SOCKET_PONG_TIMEOUT_MS) {
        closeSocket();
        connectSocket();
      }
    };

    window.addEventListener("online", restoreSocketConnection);
    window.addEventListener("focus", restoreSocketConnection);
    document.addEventListener("visibilitychange", restoreSocketConnection);

    return () => {
      shouldReconnectRef.current = false;
      clearReconnectTimeout();
      clearHeartbeatTimers();
      window.removeEventListener("online", restoreSocketConnection);
      window.removeEventListener("focus", restoreSocketConnection);
      document.removeEventListener("visibilitychange", restoreSocketConnection);
      closeSocket();
    };
  }, [
    clearHeartbeatTimers,
    clearReconnectTimeout,
    closeSocket,
    connectSocket,
  ]);

  const markAsReadMutation = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
      });
    },
    onError: (error, notificationId) => {
      console.error("Failed to mark notification as read", error);

      setLocallyReadIds((previousIds) =>
        previousIds.filter((id) => id !== notificationId)
      );
    },
  });

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNotificationClick = useCallback(
    (notification) => {
      const notificationId = getNotificationId(notification);
      const route = getNotificationRoute(notification);
      const shouldMarkAsRead =
        notificationId !== undefined &&
        notificationId !== null &&
        !notification.is_read;

      if (shouldMarkAsRead) {
        setLocallyReadIds((previousIds) =>
          previousIds.includes(notificationId)
            ? previousIds
            : [...previousIds, notificationId]
        );

        markAsReadMutation.mutate(notificationId);
      }

      setAnchorEl(null);

      if (route) {
        navigate(route);
      }
    },
    [markAsReadMutation, navigate]
  );

  return {
    anchorEl,
    isError,
    isLoading,
    items,
    open: Boolean(anchorEl),
    refetch,
    unreadCount,
    handleClose,
    handleNotificationClick,
    handleOpen,
    isNotificationUnread: (notification) => {
      const notificationId = getNotificationId(notification);

      return !notification.is_read && !locallyReadIds.includes(notificationId);
    },
  };
}
